

import { useState, useEffect, useRef } from "react"
import {
  FolderIcon,
  FileIcon,
  MusicIcon,
  FileTextIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  RefreshCwIcon,
  HomeIcon,
  MenuIcon,
  XIcon,
  FolderOpenIcon,
  ArrowLeftIcon,
  SearchIcon,
  LayoutGridIcon,
  LayoutListIcon,
  Loader2Icon,
  AlertCircleIcon,
  ChevronLeftIcon,
} from "lucide-react"
import PdfViewerWithChat from "./pdf-viewer-with-chat"
import AudioPlayer from "./audio-player"
import { Helmet } from "react-helmet"
const S3FileExplorer = () => {
  const [files, setFiles] = useState([])
  const [folders, setFolders] = useState([])
  const [currentPath, setCurrentPath] = useState("/")
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: "Home", path: "/" }])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [expandedFolders, setExpandedFolders] = useState({})
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [viewMode, setViewMode] = useState("grid") // "list" or "grid"
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [animatingFolder, setAnimatingFolder] = useState(null)
  const [isGlobalSearch, setIsGlobalSearch] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimeoutRef = useRef(null)

// Const for meta
  const pageUrl   = 'https://www.aiatedu.com/past-papers'
  const pageTitle = 'Past Papers | AIAT'
  const pageDesc  =
    'Browse and interact with A-level, AP, and IB past exam papers on AIAT. Includes PDF viewer, download, annotation, and AI chat assistance.'
  useEffect(() => {
    if (!isGlobalSearch) {
      fetchFilesAndFolders(currentPath)
    }
  }, [currentPath, isGlobalSearch])

  useEffect(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        performGlobalSearch(searchQuery)
      }, 500) // Debounce for 500ms
    } else if (searchQuery.length === 0 && isGlobalSearch) {
      setIsGlobalSearch(false)
      fetchFilesAndFolders(currentPath)
    }

    return () => {
      // Clean up timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  const fetchFilesAndFolders = async (path) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/s3?path=${encodeURIComponent(path)}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch files: ${response.statusText}`)
      }

      const data = await response.json()

      setFiles(data.files || [])
      setFolders(data.folders || [])
    } catch (err) {
      console.error("Error fetching from S3:", err)
      setError(err.message || "Failed to fetch files from S3")
    } finally {
      setLoading(false)
    }
  }

  const performGlobalSearch = async (query) => {
    if (query.length < 2) return

    setSearchLoading(true)
    setError(null)

    try {
      const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL
      const searchUrl = serverUrl
        ? `${serverUrl}/api/s3/search?query=${encodeURIComponent(query)}`
        : `/api/s3/search?query=${encodeURIComponent(query)}`

      const response = await fetch(searchUrl)

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`)
      }

      const data = await response.json()

      setFiles(data.files || [])
      setFolders(data.folders || [])
      setIsGlobalSearch(true)
    } catch (err) {
      console.error("Search error:", err)
      setError(err.message || "Failed to search files")
    } finally {
      setSearchLoading(false)
    }
  }

  const navigateToFolder = (folderPath, folderName) => {
    if (isGlobalSearch) {
      setSearchQuery("")
      setIsGlobalSearch(false)
    }

    setAnimatingFolder(folderName)

    setTimeout(() => {
      const newPath = folderPath.endsWith("/") ? folderPath : `${folderPath}/`
      setCurrentPath(newPath)

      const parts = newPath.split("/").filter(Boolean)
      const newBreadcrumbs = [{ name: "Home", path: "/" }]

      let currentBuildPath = "/"
      parts.forEach((part) => {
        currentBuildPath += part + "/"
        newBreadcrumbs.push({
          name: part,
          path: currentBuildPath,
        })
      })

      setBreadcrumbs(newBreadcrumbs)
      setSelectedFile(null)
      setAnimatingFolder(null)
    }, 300) 
  }

  const navigateToBreadcrumb = (path) => {

    if (isGlobalSearch) {
      setSearchQuery("")
      setIsGlobalSearch(false)
    }

    setCurrentPath(path)


    const index = breadcrumbs.findIndex((b) => b.path === path)
    if (index !== -1) {
      setBreadcrumbs(breadcrumbs.slice(0, index + 1))
    }

    setSelectedFile(null)
  }

  const handleFileSelect = (file) => {
    setSelectedFile(file)
  }

  const toggleFolder = (folderId, folderName) => {
    setAnimatingFolder(folderName)

    setTimeout(() => {
      setExpandedFolders((prev) => ({
        ...prev,
        [folderId]: !prev[folderId],
      }))
      setAnimatingFolder(null)
    }, 150)
  }

  const refreshCurrentFolder = () => {
    if (isGlobalSearch && searchQuery) {
      performGlobalSearch(searchQuery)
    } else {
      fetchFilesAndFolders(currentPath)
    }
  }

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible)
  }

  const toggleViewMode = () => {
    setViewMode(viewMode === "list" ? "grid" : "list")
  }

  const clearSearch = () => {
    setSearchQuery("")
    setIsGlobalSearch(false)
    fetchFilesAndFolders(currentPath)
  }

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()

    if (extension === "mp3") return <MusicIcon className="h-5 w-5 text-purple-500" />
    if (extension === "pdf") return <FileTextIcon className="h-5 w-5 text-red-500" />
    return <FileIcon className="h-5 w-5 text-blue-500" />
  }

  const getFileType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()
    return extension
  }

  const getFileColor = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase()

    switch (extension) {
      case "pdf":
        return "from-red-500 to-red-600"
      case "mp3":
        return "from-purple-500 to-purple-600"
      case "jpg":
      case "jpeg":
      case "png":
        return "from-green-500 to-green-600"
      case "doc":
      case "docx":
        return "from-blue-500 to-blue-600"
      case "xls":
      case "xlsx":
        return "from-emerald-500 to-emerald-600"
      case "ppt":
      case "pptx":
        return "from-orange-500 to-orange-600"
      default:
        return "from-gray-500 to-gray-600"
    }
  }

  const renderFilePreview = () => {
    if (!selectedFile) return null

    const fileType = getFileType(selectedFile.name)
    const fileUrl = selectedFile.url

    switch (fileType) {
      case "pdf":
        return <PdfViewerWithChat file={fileUrl} />
      case "mp3":
        return <AudioPlayer file={fileUrl} fileName={selectedFile.name} />
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-lg mb-4">File: {selectedFile.name}</p>
            <a
              href={fileUrl}
              download={selectedFile.name}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
            >
              Download File
            </a>
          </div>
        )
    }
  }

  // Filter files and folders based on search query for local filtering
  const filteredFolders =
    !isGlobalSearch && searchQuery
      ? folders.filter((folder) => folder.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : folders

  const filteredFiles =
    !isGlobalSearch && searchQuery
      ? files.filter((file) => file.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : files

  return (
    <>
    <Helmet>
      {/* 1. CMP */}
      <script
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
        data-settings-id="R7Xi1xeFRKhrKk"
        async
      />
      {/* 2. gtag */}
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=AW-17303305286"
      />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17303305286');
      `}</script>
      {/* 3a. GTM head */}
      <script>{`
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MBKBFC9M');
      `}</script>
        {/* Browser tab title */}
        <title>{pageTitle}</title>

        {/* Basic SEO */}
        <meta name="description" content={pageDesc} />
        <meta
          name="keywords"
          content="past papers, A-level, AP, IB, AI tutor, exam prep, CAIE, PDF viewer"
        />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />

        {/* Open Graph */}
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url"         content={pageUrl} />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
    <div className="flex min-h-screen max-h-full border rounded-lg overflow-hidden bg-gray-50 shadow-lg">
      {/* Left sidebar - File explorer */}
      <div
        className={`border-r bg-white flex flex-col transition-all duration-300 ease-in-out ${
          sidebarVisible ? "w-1/4 opacity-100 translate-x-0" : "w-0 opacity-0 -translate-x-full"
        }`}
      >
        <div className="p-3 border-b bg-gradient-to-r from-gray-500 to-gray-700 text-white flex items-center justify-between">
          <h2 className="font-semibold">Files Explorer</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => navigateToFolder("/")}
              className="p-1 rounded-full hover:bg-blue-400 transition-all hover:scale-110 active:scale-95"
              title="Home"
            >
              <HomeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={refreshCurrentFolder}
              className="p-1 rounded-full hover:bg-blue-400 transition-all hover:scale-110 active:scale-95"
              title="Refresh"
            >
              <RefreshCwIcon className="h-4 w-4" />
            </button>
            <button
              onClick={toggleViewMode}
              className="p-1 rounded-full hover:bg-blue-400 transition-all hover:scale-110 active:scale-95"
              title={viewMode === "list" ? "Grid view" : "List view"}
            >
              {viewMode === "list" ? <LayoutGridIcon className="h-4 w-4" /> : <LayoutListIcon className="h-4 w-4" />}
            </button>
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-full hover:bg-blue-400 transition-all hover:scale-110 active:scale-95"
              title={sidebarVisible ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarVisible ? <ChevronLeftIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className={`p-2 bg-gray-50 transition-all duration-300 ${isSearchFocused ? "shadow-md" : ""}`}>
          <div
            className={`flex items-center bg-white rounded-full border overflow-hidden transition-all ${
              isSearchFocused ? "border-blue-400 shadow-sm" : "border-gray-200"
            }`}
          >
            {searchLoading ? (
              <Loader2Icon className="h-4 w-4 ml-3 text-blue-500 animate-spin" />
            ) : (
              <SearchIcon
                className={`h-4 w-4 ml-3 transition-colors ${isSearchFocused ? "text-blue-500" : "text-gray-400"}`}
              />
            )}
            <input
              type="text"
              placeholder={isGlobalSearch ? "Searching all files..." : "Search files..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className="w-full py-2 px-3 border-gray-100 focus:outline-none text-sm"
            />
            {searchQuery && (
              <button onClick={clearSearch} className="mr-2 text-gray-400 hover:text-gray-600">
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Search mode indicator */}
          {isGlobalSearch && (
            <div className="flex items-center justify-between mt-2 px-2 py-1 bg-blue-50 rounded-full text-xs text-blue-700">
              <span>Searching across all folders</span>
              <button onClick={clearSearch} className="text-blue-600 hover:text-blue-800 font-medium">
                Exit Search
              </button>
            </div>
          )}
        </div>

        {/* Breadcrumbs - only show when not in global search */}
        {!isGlobalSearch && (
          <div className="flex items-center p-2 bg-gray-50 overflow-x-auto whitespace-nowrap">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && <ChevronRightIcon className="h-4 w-4 mx-1 text-gray-400" />}
                <button
                  onClick={() => navigateToBreadcrumb(crumb.path)}
                  className={`text-sm px-2 py-1 rounded-full transition-all hover:bg-gray-200 ${
                    index === breadcrumbs.length - 1 ? "font-medium text-blue-600 bg-blue-50" : "text-gray-600"
                  }`}
                >
                  {index === 0 ? <HomeIcon className="h-3 w-3 inline mr-1" /> : null}
                  {crumb.name}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Back button for mobile/small screens - only show when not in global search */}
        {!isGlobalSearch && currentPath !== "/" && (
          <button
            onClick={() => {
              const parentPath = currentPath.split("/").slice(0, -2).join("/") + "/"
              navigateToBreadcrumb(parentPath || "/")
            }}
            className="md:hidden flex items-center text-sm text-blue-600 p-2 hover:bg-blue-50 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to parent folder
          </button>
        )}

        {/* File list */}
        <div className="flex-1 max-h-[80vh] overflow-y-auto p-2">
          {loading || searchLoading ? (
            <div className="flex flex-col justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
              <p className="text-sm text-gray-500">{searchLoading ? "Searching..." : "Loading..."}</p>
            </div>
          ) : error ? (
            <div className="text-red-500 p-3 text-center bg-red-50 rounded-lg m-2 border border-red-200">
              <AlertCircleIcon className="h-6 w-6 mx-auto mb-2" />
              <p className="mb-2">{error}</p>
              <button
                onClick={refreshCurrentFolder}
                className="px-3 py-1 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors inline-flex items-center"
              >
                <RefreshCwIcon className="h-3 w-3 mr-1" />
                Try Again
              </button>
            </div>
          ) : (
            <div>
              {/* Search results header for global search */}
              {isGlobalSearch && (
                <div className="mb-3 px-2">
                  <h3 className="text-sm font-medium text-blue-600">Search results for "{searchQuery}"</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Found {folders.length} folders and {files.length} files
                  </p>
                </div>
              )}

              {/* Folders */}
              {filteredFolders.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">Folders</h3>

                  {viewMode === "list" ? (
                    // List view for folders
                    <div className="space-y-1">
                      {filteredFolders.map((folder, index) => (
                        <div key={index} className="mb-1 group">
                          <button
                            onClick={() =>
                              isGlobalSearch
                                ? navigateToFolder(folder.path, folder.name)
                                : toggleFolder(`folder-${index}`, folder.name)
                            }
                            className="flex items-center w-full text-left px-2 py-2 rounded-lg hover:bg-blue-50 transition-all group-hover:translate-x-1"
                          >
                            {!isGlobalSearch &&
                              (expandedFolders[`folder-${index}`] ? (
                                <ChevronDownIcon className="h-4 w-4 mr-1 text-gray-500 transition-transform" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 mr-1 text-gray-500 transition-transform" />
                              ))}

                            {animatingFolder === folder.name ? (
                              <FolderOpenIcon className="h-5 w-5 mr-2 text-yellow-500 animate-pulse" />
                            ) : !isGlobalSearch && expandedFolders[`folder-${index}`] ? (
                              <FolderOpenIcon className="h-5 w-5 mr-2 text-yellow-500" />
                            ) : (
                              <FolderIcon className="h-5 w-5 mr-2 text-yellow-500" />
                            )}

                            <span className="truncate font-medium">{folder.name}</span>
                          </button>

                          {!isGlobalSearch && expandedFolders[`folder-${index}`] && (
                            <div className="ml-6 mt-1 border-l-2 border-blue-200 pl-2 animate-fadeIn">
                              <button
                                onClick={() => navigateToFolder(folder.path, folder.name)}
                                className="flex items-center w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-all hover:translate-x-1"
                              >
                                <span className="mr-2">📂</span>
                                Open Folder
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Grid view for folders
                    <div className="grid grid-cols-2 gap-2">
                      {filteredFolders.map((folder, index) => (
                        <div
                          key={index}
                          className="group relative"
                          onClick={() => navigateToFolder(folder.path, folder.name)}
                        >
                          <div className="flex flex-col items-center p-3 bg-yellow-50 rounded-lg border border-yellow-100 hover:bg-yellow-100 hover:border-yellow-200 transition-all hover:shadow-md cursor-pointer group-hover:scale-105">
                            {animatingFolder === folder.name ? (
                              <FolderOpenIcon className="h-10 w-10 text-yellow-500 animate-pulse mb-2" />
                            ) : (
                              <FolderIcon className="h-10 w-10 text-yellow-500 mb-2 group-hover:scale-110 transition-transform" />
                            )}
                            <span className="text-xs text-center font-medium truncate w-full">{folder.name}</span>

                            {/* Show folder path for global search results */}
                            {isGlobalSearch && folder.path && (
                              <span className="text-xs text-gray-500 truncate w-full mt-1">{folder.path}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Files */}
              {filteredFiles.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2">Files</h3>

                  {viewMode === "list" ? (
                    // List view for files
                    <div className="space-y-1">
                      {filteredFiles.map((file, index) => (
                        <button
                          key={index}
                          onClick={() => handleFileSelect(file)}
                          className={`flex items-center w-full text-left px-2 py-2 rounded-lg transition-all group hover:translate-x-1 ${
                            selectedFile && selectedFile.name === file.name
                              ? "bg-blue-100 text-blue-700"
                              : "hover:bg-blue-50"
                          }`}
                        >
                          <div
                            className={`flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-r ${getFileColor(file.name)} text-white mr-2 group-hover:scale-110 transition-transform`}
                          >
                            {getFileIcon(file.name)}
                          </div>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="truncate font-medium">{file.name}</span>
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="mr-2">{(file.size / 1024).toFixed(1)} KB</span>

                              {/* Show folder path for global search results */}
                              {isGlobalSearch && file.folderPath && <span className="truncate">{file.folderPath}</span>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    // Grid view for files
                    <div className="grid grid-cols-2 gap-2">
                      {filteredFiles.map((file, index) => (
                        <div
                          key={index}
                          className={`group relative ${
                            selectedFile && selectedFile.name === file.name ? "ring-2 ring-blue-500 rounded-lg" : ""
                          }`}
                          onClick={() => handleFileSelect(file)}
                        >
                          <div className="flex flex-col items-center p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all hover:shadow-md cursor-pointer group-hover:scale-105">
                            <div
                              className={`flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r ${getFileColor(file.name)} text-white mb-2 group-hover:scale-110 transition-transform`}
                            >
                              {getFileIcon(file.name)}
                            </div>
                            <span className="text-xs text-center font-medium truncate w-full">{file.name}</span>
                            <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>

                            {/* Show folder path for global search results */}
                            {isGlobalSearch && file.folderPath && (
                              <span className="text-xs text-gray-500 truncate w-full mt-1">{file.folderPath}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200 m-2">
                  {searchQuery ? (
                    <>
                      <SearchIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>No files match your search</p>
                      {isGlobalSearch && (
                        <button
                          onClick={clearSearch}
                          className="mt-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors inline-flex items-center"
                        >
                          <ArrowLeftIcon className="h-3 w-3 mr-1" />
                          Return to browsing
                        </button>
                      )}
                    </>
                  ) : folders.length > 0 ? (
                    <>
                      <FileIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>No files in this folder</p>
                    </>
                  ) : (
                    <>
                      <FolderIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>This folder is empty</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right side - File preview */}
      <div className={`bg-white transition-all duration-300 ease-in-out ${sidebarVisible ? "w-3/4" : "w-full"}`}>
        {/* Show a toggle button when sidebar is collapsed */}
        {!sidebarVisible && (
          <button
            onClick={toggleSidebar}
            className="absolute top-24 left-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-all hover:scale-110 active:scale-95"
            aria-label="Show sidebar"
          >
            <MenuIcon className="h-5 w-5 text-gray-700" />
          </button>
        )}

        {selectedFile ? (
          renderFilePreview()
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gradient-to-b from-gray-50 to-gray-100">
            <div className="p-8 bg-white rounded-xl shadow-md border border-gray-200 flex flex-col items-center max-w-md animate-fadeIn">
              <FileIcon className="h-16 w-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium text-gray-600 mb-2">Select a file to preview</p>
              <p className="text-sm text-gray-500 text-center">Choose a file from the explorer to view its contents</p>
            </div>
          </div>
        )}
      </div>

      {/* Add global animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        .animate-pulse {
          animation: pulse 0.5s ease-in-out infinite;
        }
      `}</style>
    </div>
    </>
  )
}

export default S3FileExplorer

